import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { FaEdit, FaTrash, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import './CompaniesManagePage.css';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/ConfirmationModal';

const EMPTY_COMPANY = { id: null, name: '', is_mother_company: false, sso_redirect_uris_text: '' };

const CompaniesManagePage = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCompany, setCurrentCompany] = useState(EMPTY_COMPANY);
    const [generatedToken, setGeneratedToken] = useState('');
    const [tokenMessage, setTokenMessage] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({ title: '', message: '', onConfirm: null, variant: 'primary' });

    const triggerConfirm = (title, message, onConfirm, variant = 'primary') => {
        setConfirmConfig({ title, message, onConfirm, variant });
        setShowConfirmModal(true);
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/companies-crud');
            setCompanies(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load companies.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (company = null) => {
        if (company) {
            setCurrentCompany({
                id: company.id,
                name: company.name,
                is_mother_company: !!company.is_mother_company,
                sso_redirect_uris_text: (company.sso_redirect_uris || []).join('\n'),
            });
        } else {
            setCurrentCompany(EMPTY_COMPANY);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentCompany(EMPTY_COMPANY);
        setError(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const payload = {
            name: currentCompany.name,
            is_mother_company: currentCompany.is_mother_company,
            sso_redirect_uris: currentCompany.sso_redirect_uris_text
                .split('\n')
                .map((u) => u.trim())
                .filter(Boolean),
        };
        try {
            let res;
            if (currentCompany.id) {
                res = await api.put(`/api/companies-crud/${currentCompany.id}`, payload);
                handleCloseModal();
            } else {
                res = await api.post('/api/companies-crud', payload);
                handleCloseModal();
                if (res.data?.api_token) {
                    setGeneratedToken(res.data.api_token);
                    setTokenMessage(res.data.api_token_message || 'Please copy the API token now. It will not be shown again.');
                }
            }
            fetchCompanies();
        } catch (err) {
            const errors = err.response?.data?.errors || {};
            const uriError = Object.keys(errors).find((k) => k.startsWith('sso_redirect_uris'));
            setError((uriError && errors[uriError][0]) || err.response?.data?.message || 'Failed to save company.');
        }
    };

    const handleRegenerateToken = (company) => {
        triggerConfirm(
            "Regenerate API Token",
            `Are you sure you want to generate a new API token for ${company.name}? Any existing token for this company will be invalidated.`,
            async () => {
                try {
                    const res = await api.post(`/api/companies-crud/${company.id}/token`);
                    if (res.data?.api_token) {
                        setGeneratedToken(res.data.api_token);
                        setTokenMessage(res.data.api_token_message || 'Please copy the API token now. It will not be shown again.');
                    }
                    fetchCompanies();
                    toast.success('Token regenerated successfully!');
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to generate token.');
                }
            },
            "warning"
        );
    };

    const handleDelete = (company) => {
        triggerConfirm(
            "Delete Company",
            `Are you sure you want to delete ${company.name}?`,
            async () => {
                try {
                    await api.delete(`/api/companies-crud/${company.id}`);
                    fetchCompanies();
                    toast.success(`${company.name} deleted successfully.`);
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to delete company.');
                }
            },
            "danger"
        );
    };

    if (loading) return <div className="p-4 text-center">Loading companies...</div>;

    return (
        <div className="companies-manage-page">
            <div className="page-header">
                <h2>Manage Companies</h2>
                <button className="add-btn" onClick={() => handleOpenModal()}>
                    <FaPlus /> Add Company
                </button>
            </div>

            {error && <div className="error-alert">{error}</div>}

            <div className="table-container">
                <table className="companies-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Company Name</th>
                            <th>Mother Company</th>
                            <th>API Token Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {companies.map((company) => (
                            <tr key={company.id}>
                                <td>{company.id}</td>
                                <td className="company-name">{company.name}</td>
                                <td>
                                    {company.is_mother_company ? (
                                        <span className="badge true-badge"><FaCheck /> Yes</span>
                                    ) : (
                                        <span className="badge false-badge"><FaTimes /> No</span>
                                    )}
                                </td>
                                <td>
                                    {!company.is_mother_company ? (
                                        company.has_token ? (
                                            <span className="badge true-badge">Active</span>
                                        ) : (
                                            <span className="badge false-badge">Not Generated</span>
                                        )
                                    ) : (
                                        <span className="text-muted small">-</span>
                                    )}
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        {!company.is_mother_company && (
                                            <button 
                                                className="token-btn" 
                                                onClick={() => handleRegenerateToken(company)} 
                                                title={company.has_token ? "Regenerate API Token" : "Generate API Token"}
                                            >
                                                <FaPlus /> Token
                                            </button>
                                        )}
                                        <button className="edit-btn" onClick={() => handleOpenModal(company)} title="Edit">
                                            <FaEdit />
                                        </button>
                                        <button className="delete-btn" onClick={() => handleDelete(company)} title="Delete">
                                            <FaTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {companies.length === 0 && (
                            <tr>
                                <td colSpan="5" className="text-center">No companies found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{currentCompany.id ? 'Edit Company' : 'Add New Company'}</h3>
                        <form onSubmit={handleSave}>
                            <div className="form-group">
                                <label>Company Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={currentCompany.name}
                                    onChange={(e) => setCurrentCompany({ ...currentCompany, name: e.target.value })}
                                    required
                                    placeholder="Enter company name"
                                />
                            </div>
                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={currentCompany.is_mother_company}
                                        onChange={(e) => setCurrentCompany({ ...currentCompany, is_mother_company: e.target.checked })}
                                    />
                                    Is Mother Company?
                                </label>
                                <small className="hint">Note: Setting this will unset any existing mother company.</small>
                            </div>
                            {!currentCompany.is_mother_company && (
                                <div className="form-group">
                                    <label>SSO Redirect URIs</label>
                                    <textarea
                                        rows={3}
                                        value={currentCompany.sso_redirect_uris_text}
                                        onChange={(e) => setCurrentCompany({ ...currentCompany, sso_redirect_uris_text: e.target.value })}
                                        placeholder="https://example.com/sso-callback"
                                        style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
                                    />
                                    <small className="hint">
                                        One URL per line. Exact callback URLs this company's site may use for "Login with Iconic Unity". Must be https.
                                    </small>
                                </div>
                            )}
                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={handleCloseModal}>Cancel</button>
                                <button type="submit" className="save-btn">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {generatedToken && (
                <div className="modal-overlay">
                    <div className="modal-content token-modal">
                        <h3 style={{ color: '#10b981', marginBottom: '10px' }}>API Token Generated</h3>
                        <p className="warning-text" style={{ marginBottom: '20px' }}>{tokenMessage}</p>
                        <div className="token-display-box" style={{ marginBottom: '20px' }}>
                            <code>{generatedToken}</code>
                            <button 
                                className="copy-btn" 
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText(generatedToken);
                                    toast.success('Token copied to clipboard!');
                                }}
                            >
                                Copy
                            </button>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={() => {
                                setGeneratedToken('');
                                setTokenMessage('');
                            }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {showConfirmModal && (
                <ConfirmationModal
                    show={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={() => {
                        setShowConfirmModal(false);
                        if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                    }}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    variant={confirmConfig.variant}
                />
            )}
        </div>
    );
};

export default CompaniesManagePage;
